import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePathEditor } from "@/hooks/usePathEditor";
import { useAutoSave } from "@/hooks/useAutoSave";
import { PATH_TYPES } from "@/types/path";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PathHabitsEditor } from "@/components/paths/PathHabitsEditor";
import { PathResourcesEditor } from "@/components/paths/PathResourcesEditor";
import type { PathResources } from "@/components/paths/PathResourcesEditor";
import { PathPrerequisitesEditor } from "@/components/paths/PathPrerequisitesEditor";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Save, Send, Plus, GripVertical, Loader2, MoreVertical, Trash2, Clock, AlertTriangle, Settings2, Heart, Link as LinkIcon, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { EditorPathActivity } from "@/types/path";
import { PageHelpButton } from "@/components/PageHelpButton";

/* ─── Sortable Activity Card (simplified) ─────────────── */

function SortableActivityCard({
  activity,
  disabled,
  onNavigate,
  onRemove,
}: {
  activity: EditorPathActivity;
  disabled: boolean;
  onNavigate: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: activity.localId,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="border-border/50 cursor-pointer hover:bg-accent/30 transition-colors" onClick={onNavigate}>
        <CardHeader className="py-4">
          <div className="flex items-center gap-3">
            {!disabled && (
              <button
                {...attributes}
                {...listeners}
                className="cursor-grab touch-none text-muted-foreground hover:text-foreground shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical className="h-4 w-4" />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base">{activity.activityTitle}</CardTitle>
            </div>
            {activity.activityDurationMinutes != null && activity.activityDurationMinutes > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                <Clock className="h-3 w-3" />
                {activity.activityDurationMinutes} min
              </div>
            )}
            {!disabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove();
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Supprimer l'activité
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardHeader>
      </Card>
    </div>
  );
}

/* ─── Path Editor Page ────────────────────────────────── */

export default function PathEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const { formData, setFormData, activities, setActivities, isLoading, isSaving, isPublished, save } = usePathEditor(
    isNew ? undefined : id,
  );

  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EditorPathActivity | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [attempted, setAttempted] = useState(false);

  const titleError = attempted && !formData.title.trim();

  // Auto-save
  const autoSaveFn = useCallback(async () => {
    if (!formData.title.trim()) return;
    try {
      const result = await save(false);
      setDirty(false);
      if (isNew && result) {
        navigate(`/paths/${result.id}`, { replace: true });
      }
    } catch {
      // handled by hook
    }
  }, [formData.title, save, isNew, navigate]);

  const { triggerAutoSave, cancelAutoSave, isAutoSaving } = useAutoSave(autoSaveFn);

  const { data: themes } = useQuery({
    queryKey: ["themes"],
    queryFn: async () => {
      const { data, error } = await supabase.from("theme").select("*").order("order_index");
      if (error) throw error;
      return data;
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const markDirty = () => {
    if (!dirty) setDirty(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setActivities((prev) => {
      const oldIdx = prev.findIndex((a) => a.localId === active.id);
      const newIdx = prev.findIndex((a) => a.localId === over.id);
      const next = [...prev];
      const [moved] = next.splice(oldIdx, 1);
      next.splice(newIdx, 0, moved);
      return next;
    });
    markDirty();
    triggerAutoSave();
  };

  const deleteActivity = async (activity: EditorPathActivity) => {
    setIsDeleting(true);
    try {
      const activityId = activity.activityId;

      // 1. Delete path_activity link if it exists in DB
      if (activity.existingId) {
        await supabase.from("path_activity").delete().eq("id", activity.existingId);
      }

      // 2. Get all steps for this activity
      const { data: steps } = await supabase
        .from("activity_step")
        .select("id, card_id, card_group_id")
        .eq("activity_id", activityId);

      if (steps && steps.length > 0) {
        const stepIds = steps.map((s) => s.id);
        const cardIds = steps.map((s) => s.card_id).filter(Boolean) as string[];
        const groupIds = steps.map((s) => s.card_group_id).filter(Boolean) as string[];

        // Get cards from card_group_items
        if (groupIds.length > 0) {
          const { data: groupItems } = await supabase
            .from("card_group_item")
            .select("card_id")
            .in("card_group_id", groupIds);
          if (groupItems) {
            for (const gi of groupItems) cardIds.push(gi.card_id);
          }
          // Delete card_group_items
          await supabase.from("card_group_item").delete().in("card_group_id", groupIds);
        }

        // Delete transitions
        await supabase.from("activity_step_transition").delete().in("from_activity_step_id", stepIds);
        await supabase.from("activity_step_transition").delete().in("to_activity_step_id", stepIds);

        // Clear first_step_id reference before deleting steps
        await supabase.from("activity").update({ first_step_id: null }).eq("id", activityId);

        // Delete steps
        await supabase.from("activity_step").delete().in("id", stepIds);

        // Delete cards
        if (cardIds.length > 0) {
          await supabase.from("card").delete().in("id", cardIds);
        }

        // Delete card_groups
        if (groupIds.length > 0) {
          await supabase.from("card_group").delete().in("id", groupIds);
        }
      } else {
        // Still clear first_step_id in case
        await supabase.from("activity").update({ first_step_id: null }).eq("id", activityId);
      }

      // 3. Delete the activity itself
      await supabase.from("activity").delete().eq("id", activityId);

      // 4. Remove from local state
      setActivities((prev) => prev.filter((a) => a.localId !== activity.localId));
      markDirty();
      toast.success("Activité supprimée");
    } catch (err: any) {
      toast.error(err.message || "Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleCreateActivity = async () => {
    if (isNew) {
      setAttempted(true);
      if (!formData.title.trim()) {
        toast.error("Veuillez renseigner le titre du parcours avant d'ajouter une activité");
        return;
      }
      try {
        const result = await save(false);
        setDirty(false);
        if (result) {
          window.history.replaceState(null, "", `/paths/${result.id}`);
          navigate(`/activities/new?pathId=${result.id}`);
        }
      } catch {
        // error handled by hook
      }
      return;
    }
    navigate(`/activities/new?pathId=${id}`);
  };

  const goBackToList = useCallback(() => {
    // If there's browser history, go back to preserve search params
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/paths");
    }
  }, [navigate]);

  const handleSave = async (publish: boolean) => {
    setAttempted(true);
    try {
      cancelAutoSave();
      const result = await save(publish);
      setDirty(false);
      if (isNew && result) {
        navigate(`/paths/${result.id}`, { replace: true });
      }
      if (publish) {
        goBackToList();
      }
    } catch {
      // error handled by hook
    }
  };

  const handleClose = () => {
    if (dirty) {
      setShowCloseConfirm(true);
    } else {
      goBackToList();
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div data-tour="path-editor-title">
            <h1 className="text-2xl font-bold tracking-tight">
              {isNew ? "Nouveau parcours" : formData.title || "Parcours"}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              {isPublished && <Badge variant="default">Publié</Badge>}
              {!isNew && !isPublished && <Badge variant="secondary">Brouillon</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3" data-tour="path-editor-actions">
          <PageHelpButton tourKey="path-editor" />
          {(() => {
            const total = activities.reduce((sum, a) => sum + (a.activityDurationMinutes || 0), 0);
            return (
              <Badge variant="outline" className="gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                Durée estimée : {total} min
              </Badge>
            );
          })()}
          {!isPublished ? (
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => handleSave(false)}
                disabled={isSaving || !dirty}
                className={isAutoSaving ? "animate-pulse" : ""}
              >
                {isSaving || isAutoSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                {isAutoSaving ? "Sauvegarde…" : "Sauvegarder"}
              </Button>
              <Button onClick={() => setShowPublishConfirm(true)} disabled={isSaving}>
                <Send className="mr-2 h-4 w-4" />
                Publier
              </Button>
            </div>
          ) : dirty && (
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={isSaving}
              className={isAutoSaving ? "animate-pulse" : ""}
            >
              {isSaving || isAutoSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {isAutoSaving ? "Sauvegarde…" : "Sauvegarder"}
            </Button>
          )}
        </div>
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activities list */}
        <div className="lg:col-span-2 space-y-4" data-tour="path-editor-activities">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Activités du parcours</h2>
            {!isPublished && (
              <Button variant="outline" size="sm" onClick={handleCreateActivity}>
                <Plus className="mr-2 h-4 w-4" />
                Créer une activité
              </Button>
            )}
          </div>

          {activities.length === 0 ? (
            <Card className="border-dashed border-border/50">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground text-sm">Aucune activité dans ce parcours</p>
                {!isPublished && (
                  <Button variant="outline" className="mt-4" size="sm" onClick={handleCreateActivity}>
                    <Plus className="mr-2 h-4 w-4" />
                    Créer une activité
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={activities.map((a) => a.localId)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {activities.map((act, idx) => (
                    <div key={act.localId} className="flex items-start gap-2">
                      <span className="text-xs text-muted-foreground w-6 text-right shrink-0 mt-5">{idx + 1}</span>
                      <div className="flex-1">
                        <SortableActivityCard
                          activity={act}
                          disabled={isPublished}
                          onNavigate={() => navigate(`/activities/${act.activityId}?pathId=${id || ""}`)}
                          onRemove={() => setDeleteTarget(act)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Sidebar params */}
        <div className="space-y-5" data-tour="path-editor-params">
          <Tabs defaultValue="params">
            <TabsList className="w-full rounded-none border-b border-border bg-transparent p-0 h-auto">
              <TabsTrigger
                value="params"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none py-2.5 gap-1.5"
              >
                <Settings2 className="h-4 w-4" />
                Paramètres
              </TabsTrigger>
              <TabsTrigger
                value="habits"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none py-2.5 gap-1.5"
              >
                <Heart className="h-4 w-4" />
                Habitudes
              </TabsTrigger>
              <TabsTrigger
                value="resources"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none py-2.5 gap-1.5"
              >
                <LinkIcon className="h-4 w-4" />
                Ressources
              </TabsTrigger>
              {formData.pathType === "path" && (
                <TabsTrigger
                  value="prerequisites"
                  className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none py-2.5 gap-1.5"
                >
                  <Lock className="h-4 w-4" />
                  Prérequis
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="params" className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label htmlFor="path-title" className={titleError ? "text-destructive" : ""}>
                  Titre <span className="text-destructive ml-1">*</span>
                </Label>
                <Input
                  id="path-title"
                  value={formData.title}
                  disabled={isPublished}
                  className={titleError ? "border-destructive ring-destructive" : ""}
                  onChange={(e) => {
                    setFormData((f) => ({ ...f, title: e.target.value }));
                    markDirty();
                    triggerAutoSave();
                  }}
                  onBlur={triggerAutoSave}
                />
              </div>

              <div className="space-y-2">
                <Label>Type de parcours</Label>
                <Select
                  value={formData.pathType}
                  disabled={isPublished}
                  onValueChange={(v) => {
                    setFormData((f) => ({ ...f, pathType: v }));
                    markDirty();
                    triggerAutoSave();
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PATH_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Thème</Label>
                <Select
                  value={formData.themeId || ""}
                  disabled={isPublished}
                  onValueChange={(v) => {
                    setFormData((f) => ({ ...f, themeId: v || null }));
                    markDirty();
                    triggerAutoSave();
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Aucun thème" />
                  </SelectTrigger>
                  <SelectContent>
                    {(themes || []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.pathType === "path" && (
                <div className="space-y-2">
                  <Label>Rang d&apos;importance initial</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={typeof (formData.metadata as any)?.suggestion_importance_rank !== "number"}
                      onClick={() => {
                        const current = (formData.metadata as any)?.suggestion_importance_rank;
                        if (typeof current !== "number" || !Number.isFinite(current)) return;
                        setFormData((f) => ({
                          ...f,
                          metadata: {
                            ...(f.metadata || {}),
                            suggestion_importance_rank: current - 1,
                          },
                        }));
                        markDirty();
                        triggerAutoSave();
                      }}
                    >
                      -
                    </Button>
                    <Input
                      type="number"
                      className="w-24 text-center"
                      placeholder="—"
                      value={
                        typeof (formData.metadata as any)?.suggestion_importance_rank === "number" &&
                        Number.isFinite((formData.metadata as any).suggestion_importance_rank)
                          ? String((formData.metadata as any).suggestion_importance_rank)
                          : ""
                      }
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        setFormData((f) => {
                          const meta = { ...(f.metadata || {}) } as Record<string, unknown>;
                          if (raw === "") {
                            delete meta.suggestion_importance_rank;
                          } else {
                            const val = parseInt(raw, 10);
                            if (Number.isFinite(val)) meta.suggestion_importance_rank = val;
                          }
                          return { ...f, metadata: meta };
                        });
                        markDirty();
                        triggerAutoSave();
                      }}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        const current = (formData.metadata as any)?.suggestion_importance_rank;
                        const next =
                          typeof current === "number" && Number.isFinite(current) ? current + 1 : 1;
                        setFormData((f) => ({
                          ...f,
                          metadata: {
                            ...(f.metadata || {}),
                            suggestion_importance_rank: next,
                          },
                        }));
                        markDirty();
                        triggerAutoSave();
                      }}
                    >
                      +
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Facultatif : utilisé uniquement pour <strong>départager les parcours à score de
                    personnalisation égal</strong> (réponses à l&apos;onboarding). Sans valeur, le parcours est
                    traité en <strong>dernier</strong> parmi les égalités côté ordre de suggestion. Ce n&apos;est{" "}
                    <strong>pas</strong> l&apos;ordre affiché pour un utilisateur dans l&apos;app : celui-ci reste{" "}
                    <strong>personnalisé par utilisateur</strong>.
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="habits" className="mt-4">
              <PathHabitsEditor pathId={isNew ? undefined : id} disabled={isPublished} />
            </TabsContent>

            <TabsContent value="resources" className="mt-4">
              <PathResourcesEditor
                resources={formData.resources as unknown as PathResources | null}
                disabled={isPublished}
                onChange={(r) => {
                  setFormData((f) => ({ ...f, resources: r as unknown as Record<string, unknown> | null }));
                  markDirty();
                  triggerAutoSave();
                }}
              />
            </TabsContent>

            {formData.pathType === "path" && (
              <TabsContent value="prerequisites" className="mt-4">
                <PathPrerequisitesEditor pathId={isNew ? undefined : id} />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {/* Publish path confirm */}
      <AlertDialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-primary" />
              Publier ce parcours ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Le parcours et toutes ses activités seront publiés et ne pourront plus être modifiés. Vous devrez créer une
              nouvelle version pour les modifier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowPublishConfirm(false);
                handleSave(true);
              }}
            >
              Publier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete activity confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette activité ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'activité « {deleteTarget?.activityTitle} » et toutes ses cartes associées seront définitivement
              supprimées. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) deleteActivity(deleteTarget);
              }}
            >
              {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close confirm */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quitter sans sauvegarder ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez des modifications non sauvegardées. Elles seront perdues si vous quittez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Rester</AlertDialogCancel>
            <AlertDialogAction onClick={() => goBackToList()}>Quitter</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
